import { stableStringify } from './quote-snapshot-diff';
import { scopeMergeKeys } from './quote-scope-merge';

export const EXTERNAL_QUOTE_VALUE_MISSING = Symbol('external-quote-value-missing');

export const LOCAL_HERO_PRESENTATION_FIELDS = new Set([
    'heroImage',
    'heroImagePosition',
    'heroImageZoom',
    'heroImageHeight',
    'heroImageAutoSelectionSuppressed',
]);

export function externalQuoteValuesEqual(left: any, right: any): boolean {
    if (left === right) return true;
    if (left === EXTERNAL_QUOTE_VALUE_MISSING || right === EXTERNAL_QUOTE_VALUE_MISSING) return false;
    try {
        return stableStringify(left) === stableStringify(right);
    } catch {
        return false;
    }
}

export function isExternalQuoteMergeRecord(value: any): boolean {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

export function findScopeIdentityMatch(item: any, rows: any[]): { status: 'none' | 'unique' | 'ambiguous'; index: number } {
    const keys = new Set(scopeMergeKeys(item));
    if (keys.size === 0) return { status: 'none', index: -1 };
    const itemId = item?.id ? String(item.id) : null;
    const matches: number[] = [];
    rows.forEach((candidate, index) => {
        // Two rows that both carry an id are the same row only when the ids
        // are equal. A Stage 4 Duplicate keeps the Stage 3 activity of the row
        // it copied, and matching on that made both rows ambiguous: the
        // Stage 4 / Stage 5 merge then added copies on every edit, and the
        // editor never showed the duplicate (review M1, 2026-10-23). A row
        // without an id still matches on its activity or code.
        const candidateId = candidate?.id ? String(candidate.id) : null;
        if (itemId && candidateId) {
            if (itemId === candidateId) matches.push(index);
            return;
        }
        if (scopeMergeKeys(candidate).some((key: string) => keys.has(key))) matches.push(index);
    });
    if (matches.length === 0) return { status: 'none', index: -1 };
    if (matches.length !== 1) return { status: 'ambiguous', index: -1 };
    return { status: 'unique', index: matches[0]! };
}

export function findSymmetricScopeIdentityMatch(
    item: any,
    itemIndex: number,
    itemRows: any[],
    candidateRows: any[],
): { status: 'none' | 'unique' | 'ambiguous'; index: number } {
    const match = findScopeIdentityMatch(item, candidateRows);
    if (match.status !== 'unique') return match;
    const reverseMatch = findScopeIdentityMatch(candidateRows[match.index], itemRows);
    if (reverseMatch.status !== 'unique' || reverseMatch.index !== itemIndex) {
        return { status: 'ambiguous', index: -1 };
    }
    return match;
}

export function mergeExternalScopeArray(localRows: any[], previousRows: any[], nextRows: any[], path: any[]): any[] {
    const merged: any[] = [];

    localRows.forEach((localItem, localIndex) => {
        const previousMatch = findSymmetricScopeIdentityMatch(
            localItem,
            localIndex,
            localRows,
            previousRows,
        );
        const nextMatch = findSymmetricScopeIdentityMatch(
            localItem,
            localIndex,
            localRows,
            nextRows,
        );

        // Missing or non-unique identity is not permission to combine two
        // different scope rows. Preserve the builder's row unchanged.
        if (previousMatch.status === 'ambiguous' || nextMatch.status === 'ambiguous') {
            merged.push(localItem);
            return;
        }

        if (previousMatch.status === 'unique' && nextMatch.status === 'none') {
            const previousItem = previousRows[previousMatch.index];
            if (!externalQuoteValuesEqual(localItem, previousItem)) merged.push(localItem);
            return;
        }

        if (previousMatch.status === 'unique' && nextMatch.status === 'unique') {
            merged.push(mergeExternalQuoteValue(
                localItem,
                previousRows[previousMatch.index],
                nextRows[nextMatch.index],
                [...path, `identity:${scopeMergeKeys(localItem)[0]}`],
            ));
            return;
        }

        // A local-only row, including a concurrent local/external addition
        // with the same identity, remains builder-owned.
        merged.push(localItem);
    });

    // Append only provable external additions. A row that existed previously
    // was deleted locally and must not be resurrected. Rows without a unique
    // identity stay out rather than being positionally grafted onto another
    // scope item.
    nextRows.forEach((nextItem, nextIndex) => {
        if (scopeMergeKeys(nextItem).length === 0) return;
        const nextSelfMatch = findScopeIdentityMatch(nextItem, nextRows);
        if (nextSelfMatch.status !== 'unique' || nextSelfMatch.index !== nextIndex) return;
        if (findScopeIdentityMatch(nextItem, localRows).status !== 'none') return;
        if (findScopeIdentityMatch(nextItem, previousRows).status !== 'none') return;
        merged.push(nextItem);
    });

    return merged;
}

export function mergeExternalQuoteValue(localValue: any, previousExternalValue: any, nextExternalValue: any, path: any[] = []): any {
    if (path[0] === 'header' && LOCAL_HERO_PRESENTATION_FIELDS.has(path[1])) {
        return localValue;
    }
    if (externalQuoteValuesEqual(previousExternalValue, nextExternalValue)) {
        return localValue;
    }
    if (
        localValue === EXTERNAL_QUOTE_VALUE_MISSING
        || externalQuoteValuesEqual(localValue, previousExternalValue)
    ) {
        return nextExternalValue;
    }

    if (
        Array.isArray(localValue)
        && Array.isArray(previousExternalValue)
        && Array.isArray(nextExternalValue)
    ) {
        if (path.length === 1 && path[0] === 'scope') {
            return mergeExternalScopeArray(
                localValue,
                previousExternalValue,
                nextExternalValue,
                path,
            );
        }
        const merged: any[] = [];
        const length = Math.max(localValue.length, previousExternalValue.length, nextExternalValue.length);
        for (let index = 0; index < length; index += 1) {
            const value = mergeExternalQuoteValue(
                index < localValue.length ? localValue[index] : EXTERNAL_QUOTE_VALUE_MISSING,
                index < previousExternalValue.length ? previousExternalValue[index] : EXTERNAL_QUOTE_VALUE_MISSING,
                index < nextExternalValue.length ? nextExternalValue[index] : EXTERNAL_QUOTE_VALUE_MISSING,
                [...path, index],
            );
            if (value !== EXTERNAL_QUOTE_VALUE_MISSING) merged.push(value);
        }
        return merged;
    }

    if (
        isExternalQuoteMergeRecord(localValue)
        && isExternalQuoteMergeRecord(previousExternalValue)
        && isExternalQuoteMergeRecord(nextExternalValue)
    ) {
        const merged = { ...localValue };
        const keys = new Set([
            ...Object.keys(localValue),
            ...Object.keys(previousExternalValue),
            ...Object.keys(nextExternalValue),
        ]);
        for (const key of keys) {
            const value = mergeExternalQuoteValue(
                Object.hasOwn(localValue, key) ? localValue[key] : EXTERNAL_QUOTE_VALUE_MISSING,
                Object.hasOwn(previousExternalValue, key)
                    ? previousExternalValue[key]
                    : EXTERNAL_QUOTE_VALUE_MISSING,
                Object.hasOwn(nextExternalValue, key)
                    ? nextExternalValue[key]
                    : EXTERNAL_QUOTE_VALUE_MISSING,
                [...path, key],
            );
            if (value === EXTERNAL_QUOTE_VALUE_MISSING) delete merged[key];
            else merged[key] = value;
        }
        return merged;
    }

    return localValue;
}
